import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { timeAgo } from '../utils/timeAgo';
import { focusQuiet } from '../utils/scroll';
import { Analytics } from '../analytics';
import { api } from '../api/client';
import { BRAND_NAME } from '../branding';
import { usePerm } from '../hooks/usePerm';
import BirthdayGreetingModal from './BirthdayGreetingModal';
import ExpenseReviewModal from './ExpenseReviewModal';
import { useChannel } from './channel/ChannelProvider';

// ─── Иконки по типу события ───────────────────────────────────────────────────
const EVENT_ICONS = {
  task_assigned:           '📋',
  task_completed:          '✅',
  task_overdue:            '⚠️',
  task_deadline:           '⏰',
  task_emergency_assigned:   '🚨',
  task_emergency_admin:      '🚨',
  task_emergency_curator:    '🚨',
  task_emergency_started:    '🚨',
  task_emergency_completed:  '🚨',
  task_emergency_escalation: '🚨',
  task_started:            '🔧',
  assignee_status_set:     '🔧',
  task_reminder:           '📅',
  curator_change_request:  '🔄',
  curator_offer:           '🔄',
  curator_change_accepted: '✅',
  curator_change_rejected: '❌',
  doc_expiry:              '📄',
  demo_invoice_offer:      '🧾',
  demo_invoice_issued:     '✉️',
  demo_invoice_paid:       '💰',
  demo_expense_offer:      '💸',
  // Сумму инвойса поправили в Zoho Books — обратный синк заметил расхождение
  zoho_invoice_changed:    '✎',
  zoho_invoice_customer_changed: '⇄',
  // Счёт отправлен клиенту письмом (шаблон «Коммуникаций» + PDF из Zoho)
  zoho_invoice_sent:       '📤',
  // Поступила оплата по Zoho-инвойсу — полная или частичная
  zoho_invoice_payment_received: '💰',
  quote_sent:              '📐',
  // Канал: упоминание, ответ, сообщение по моей Работе
  channel_message:         '💬',
  client_birthday:         '🎂',
  // Внешние заявки (Do Do Desk и др.): обычная — «входящее письмо», аварийная — «аларм».
  request_received:          '📨',
  request_emergency_received:'🚨',
  test:                    '🔔',
};

// Распарсить notif.params (строка JSON или объект).
function parseParams(notif) {
  if (!notif.params) return {};
  try { return typeof notif.params === 'string' ? JSON.parse(notif.params) : notif.params; }
  catch { return {}; }
}

// ─── Иконка-галочка ───────────────────────────────────────────────────────────
function IcoCheck({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function IcoX({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function iconBtn(color, borderColor, bgColor) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 34, height: 34, borderRadius: 8, flexShrink: 0,
    border: `1px solid ${borderColor}`, background: bgColor, color,
    cursor: 'pointer', transition: 'opacity 0.15s',
  };
}

const CHANNEL_LABELS = {
  push:      'Push',
  email:     'Email',
  telegram:  'TG',
  whatsapp:  'WhatsApp',
};

// ─── Bell button (экспортируется отдельно для Layout) ─────────────────────────
export function NotificationBell({ unread, onClick }) {
  const { t } = useTranslation();
  const prevUnread = useRef(unread);
  const [ring, setRing] = useState(false);

  // Анимируем колокольчик при росте числа непрочитанных = пришло новое уведомление.
  useEffect(() => {
    if (unread > prevUnread.current) {
      setRing(true);
      const id = setTimeout(() => setRing(false), 1700);
      prevUnread.current = unread;
      return () => clearTimeout(id);
    }
    prevUnread.current = unread;
  }, [unread]);

  return (
    <button
      className={`notif-bell${ring ? ' notif-bell--ring' : ''}`}
      onClick={onClick}
      title={t('notifications.bellTitle')}
      aria-label={`${t('notifications.bellTitle')}${unread > 0 ? `, ${unread}` : ''}`}
    >
      <span className="notif-bell-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      </span>
      {unread > 0 && (
        <span className="notif-bell-badge">{unread > 9 ? '9+' : unread}</span>
      )}
    </button>
  );
}

// ─── Дедупликация: одно событие = одна строка, каналы объединяются ───────────
function dedupeNotifications(notifications) {
  const groups = [];
  const seen = new Map();

  for (const n of notifications) {
    // Окно дедупликации — 10 минут (одно событие может писаться push+email в разные секунды)
    const window10 = Math.floor(new Date(n.sent_at).getTime() / 600000);
    const key = `${n.event_type}|${n.url || ''}|${window10}`;

    if (seen.has(key)) {
      const g = groups[seen.get(key)];
      if (!g.channels.includes(n.channel)) g.channels.push(n.channel);
      if (!n.read_at) { g.read_at = null; g.ids.push(n.id); }
      // Представитель карточки может оказаться «бедной» записью (напр. служебный
      // лог от sendEmail с пустыми params) — подтягиваем непустые поля из любой
      // записи группы, чтобы заголовок/тело рендерились корректно.
      if (!g.params && n.params) g.params = n.params;
      if (!g.title  && n.title)  g.title  = n.title;
      if (!g.body   && n.body)   g.body   = n.body;
    } else {
      seen.set(key, groups.length);
      groups.push({ ...n, channels: [n.channel], ids: [n.id] });
    }
  }
  return groups;
}

// ─── Основной drawer ──────────────────────────────────────────────────────────
export default function NotificationCenter({ open, onClose, onCloseSidebar, notifications, unread, loading, markAllRead, markOneRead, refresh }) {
  const { t, i18n } = useTranslation();
  const drawerRef = useRef(null);
  const navigate  = useNavigate();
  const channel = useChannel();
  // Локальный стейт обработанных curator_change_request уведомлений
  const [curatorActioned, setCuratorActioned] = useState({}); // { [notif.id]: 'accepted'|'rejected'|'loading' }
  // Локальный стейт обработанных demo_invoice_offer (слайс 2 демо-тура)
  const [invoiceActioned, setInvoiceActioned] = useState({}); // { [notif.id]: 'accepted'|'rejected'|'loading' }
  // Обработанные demo_expense_offer (слайс 3 демо-тура: согласование расхода)
  const [expenseActioned, setExpenseActioned] = useState({}); // { [notif.id]: 'accepted'|'rejected'|'loading' }
  const { can: canPerm } = usePerm();
  const canApproveExpenses = canPerm('expenses.approve');
  const [expenseModalNotif, setExpenseModalNotif] = useState(null); // открытая модалка просмотра чека
  // Поздравления с ДР: статус по клиенту (для ✓) + клиент для открытия модала.
  const [birthdayStatus, setBirthdayStatus] = useState({}); // { [clientId]: {greeted_this_year, ...} }
  const [birthdayClient, setBirthdayClient] = useState(null); // объект клиента для BirthdayGreetingModal

  // Подтягиваем статус поздравления для всех client_birthday уведомлений (для индикатора ✓).
  useEffect(() => {
    if (!open) return;
    const ids = [...new Set(
      notifications
        .filter(n => n.event_type === 'client_birthday')
        .map(n => parseParams(n).clientId)
        .filter(Boolean)
    )];
    ids.forEach(cid => {
      if (birthdayStatus[cid] !== undefined) return; // уже загружали
      api.getBirthdayGreetingStatus(cid)
        .then(s => setBirthdayStatus(prev => ({ ...prev, [cid]: s })))
        .catch(() => setBirthdayStatus(prev => ({ ...prev, [cid]: null })));
    });
  }, [open, notifications]); // eslint-disable-line

  const openBirthday = (notif) => {
    const cid = parseParams(notif).clientId;
    if (!cid) return;
    const s = birthdayStatus[cid];
    if (s) { setBirthdayClient(s); return; }
    // Статус ещё не подгрузился — тянем по требованию.
    api.getBirthdayGreetingStatus(cid)
      .then(st => { setBirthdayStatus(prev => ({ ...prev, [cid]: st })); setBirthdayClient(st); })
      .catch(() => {});
  };

  // ✓ — выставить инвойс (демо-стаб без Zoho API), ✗ — отклонить предложение.
  const handleInvoiceOffer = async (notif, accept) => {
    const p = notif.params ? (typeof notif.params === 'string' ? JSON.parse(notif.params) : notif.params) : {};
    setInvoiceActioned(prev => ({ ...prev, [notif.id]: 'loading' }));
    try {
      if (accept) {
        const r = await api.zohoCreateInvoice({
          task_id: p.taskId,
          client_id: p.clientId,
          items: [{ name: p.taskTitle || 'Работа', rate: Number(p.amount) || 1500, quantity: 1 }],
        });
        Analytics.demoInvoiceAccepted?.();
        window.dispatchEvent(new CustomEvent('demo-invoice-issued', {
          detail: { taskId: p.taskId, invoiceId: r?.invoice?.id, total: r?.invoice?.total },
        }));
      } else {
        Analytics.demoInvoiceDeclined?.();
        window.dispatchEvent(new CustomEvent('demo-invoice-declined', { detail: { taskId: p.taskId } }));
      }
      setInvoiceActioned(prev => ({ ...prev, [notif.id]: accept ? 'accepted' : 'rejected' }));
      await markOneRead(notif.ids);
      refresh?.();
    } catch {
      setInvoiceActioned(prev => { const n = { ...prev }; delete n[notif.id]; return n; });
    }
  };

  // Слайс 3: согласование расхода (✓ → approved, ✗ → rejected). Реальный PATCH.
  const handleExpenseOffer = async (notif, accept) => {
    const p = parseParams(notif);
    setExpenseActioned(prev => ({ ...prev, [notif.id]: 'loading' }));
    try {
      if (p.expenseId) {
        await api.patch(`/expenses/${p.expenseId}`, { status: accept ? 'approved' : 'rejected' });
      }
      if (accept) {
        Analytics.demoExpenseApproved?.();
        window.dispatchEvent(new CustomEvent('demo-expense-issued', { detail: { expenseId: p.expenseId } }));
      } else {
        Analytics.demoExpenseRejected?.();
        window.dispatchEvent(new CustomEvent('demo-expense-declined', { detail: { expenseId: p.expenseId } }));
      }
      setExpenseActioned(prev => ({ ...prev, [notif.id]: accept ? 'accepted' : 'rejected' }));
      await markOneRead(notif.ids);
      refresh?.();
    } catch {
      setExpenseActioned(prev => { const n = { ...prev }; delete n[notif.id]; return n; });
    }
  };

  const handleCuratorAction = async (notif, accept) => {
    const p = notif.params ? (typeof notif.params === 'string' ? JSON.parse(notif.params) : notif.params) : {};
    const taskId = p.taskId;
    if (!taskId) return;
    setCuratorActioned(prev => ({ ...prev, [notif.id]: 'loading' }));
    try {
      if (accept) {
        await api.acceptCuratorChange(taskId);
      } else {
        await api.rejectCuratorChange(taskId);
      }
      setCuratorActioned(prev => ({ ...prev, [notif.id]: accept ? 'accepted' : 'rejected' }));
      await markOneRead(notif.ids);
      refresh?.();
    } catch {
      setCuratorActioned(prev => { const n = { ...prev }; delete n[notif.id]; return n; });
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open && drawerRef.current) focusQuiet(drawerRef.current);
  }, [open]);

  const handleNotifClick = (notif) => {
    Analytics.notifClicked({ event_type: notif.event_type, was_unread: !notif.read_at });
    // Расход на согласование: тап раскрывает детали с чеком (модалка), пока не обработан.
    if (notif.event_type === 'demo_expense_offer' && !expenseActioned[notif.id]) {
      setExpenseModalNotif(notif);
      return;
    }
    // Инвойс-оффер: действие только через ✓ в карточке. Тап по карточке игнорируем —
    // иначе пометили бы прочитанным/ушли со страницы и тур завис бы (кнопки пропали).
    if (notif.event_type === 'demo_invoice_offer' && !invoiceActioned[notif.id]) {
      return;
    }
    if (!notif.read_at) notif.ids.forEach(id => markOneRead(id));
    // Канал: открываем шторку на сообщении, а не уходим на /tasks — navigate
    // ниже теряет query, а ?channel= — это и есть адрес сообщения.
    if (notif.event_type === 'channel_message' && channel && channel.enabled) {
      const p = parseParams(notif);
      onClose();
      onCloseSidebar?.();
      channel.openChannel({ messageId: Number(p.messageId) || null });
      return;
    }
    if (notif.url) {
      onClose();
      onCloseSidebar?.();
      const [pathname, search] = notif.url.split('?');
      const params = new URLSearchParams(search || '');
      const taskId = params.get('task');
      // Внешняя заявка: url = /requests?openRequestId=N. navigate теряет query, поэтому
      // прокидываем id через state — RequestsPage раскроет модал (читает location.state).
      const openRequestId = params.get('openRequestId');
      // «Инвойс оплачен» — открыть Работу и сразу раскрыть аккордеон Zoho (см. PropertyDetailPage).
      const openZoho = notif.event_type === 'demo_invoice_paid';
      const state = {
        ...(taskId ? { openTaskId: Number(taskId), ...(openZoho ? { openZoho: true } : {}) } : {}),
        ...(openRequestId ? { openRequestId: Number(openRequestId) } : {}),
      };
      navigate(pathname, Object.keys(state).length ? { state } : undefined);
    }
  };

  return (
    <>
      <div
        className={`notif-overlay ${open ? 'notif-overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        ref={drawerRef}
        className={`notif-drawer ${open ? 'notif-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('notifications.ariaLabel')}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="notif-header">
          <div className="notif-header-left">
            <span className="notif-header-title">{t('notifications.title')}</span>
            {unread > 0 && (
              <span className="notif-header-badge">{unread}</span>
            )}
          </div>
          <div className="notif-header-actions">
            {unread > 0 && (
              <button className="notif-read-all-btn" onClick={() => { Analytics.notifMarkAllRead(); markAllRead(); }} title={t('notifications.markAllReadTitle')}>
                {t('notifications.markAllRead')}
              </button>
            )}
            <button className="notif-close-btn" onClick={onClose} aria-label={t('notifications.close')}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="notif-list">
          {loading && notifications.length === 0 && (
            <div className="notif-empty">
              <div className="notif-empty-icon">⏳</div>
              <div>{t('notifications.loading')}</div>
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <div className="notif-empty">
              <div className="notif-empty-icon">🔔</div>
              <div className="notif-empty-title">{t('notifications.emptyTitle')}</div>
              <div className="notif-empty-sub">{t('notifications.emptySub')}</div>
            </div>
          )}

          {dedupeNotifications(notifications).map(notif => (
            <div
              key={notif.id}
              {...(notif.event_type === 'demo_expense_offer' ? { 'data-demo-target': 'demo-expense-notif' }
                : notif.event_type === 'demo_invoice_offer' ? { 'data-demo-target': 'demo-invoice-notif' } : {})}
              className={`notif-item ${!notif.read_at ? 'notif-item--unread' : ''} ${notif.url ? 'notif-item--clickable' : ''}`}
              onClick={() => handleNotifClick(notif)}
              role={notif.url ? 'button' : undefined}
              tabIndex={notif.url ? 0 : undefined}
              onKeyDown={notif.url ? (e) => { if (e.key === 'Enter') handleNotifClick(notif); } : undefined}
            >
              {!notif.read_at && <div className="notif-unread-dot" />}

              <div className="notif-item-icon">
                {EVENT_ICONS[notif.event_type] || '🔔'}
              </div>

              <div className="notif-item-content">
                {(() => {
                  const { title, body } = renderNotifText(notif, t);
                  return (
                    <>
                      <div className="notif-item-title">{title}</div>
                      {body && <div className="notif-item-body">{body}</div>}
                    </>
                  );
                })()}
                {/* Inline-кнопки для запроса смены куратора */}
                {notif.event_type === 'curator_change_request' && (() => {
                  const state = curatorActioned[notif.id];

                  // 1. Только что принял решение в этой сессии → показываем статус
                  if (state === 'accepted') return (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#22c55e', fontWeight: 500 }}>
                      ✅ {t('notifications.curatorAccepted')}
                    </div>
                  );
                  if (state === 'rejected') return (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444', fontWeight: 500 }}>
                      ❌ {t('notifications.curatorDeclined')}
                    </div>
                  );

                  // 2. Прочитано в другой сессии — читаем решение из params
                  if (notif.read_at) {
                    const decision = (notif.params
                      ? (typeof notif.params === 'string' ? JSON.parse(notif.params) : notif.params)
                      : {}).decision;
                    return (
                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 500,
                        color: decision === 'accepted' ? '#22c55e' : decision === 'rejected' ? '#ef4444' : 'var(--text3)' }}>
                        {decision === 'accepted' ? '✅ ' + t('notifications.curatorAccepted')
                          : decision === 'rejected' ? '❌ ' + t('notifications.curatorDeclined')
                          : t('notifications.curatorActionDone')}
                      </div>
                    );
                  }

                  // 3. Ещё не решено — показываем кнопки
                  return (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                      <button
                        disabled={state === 'loading'}
                        onClick={() => handleCuratorAction(notif, true)}
                        title={t('notifications.curatorAccept')}
                        style={iconBtn('#22c55e', '#22c55e44', '#22c55e18')}
                      >
                        {state === 'loading' ? '…' : <IcoCheck />}
                      </button>
                      <button
                        disabled={state === 'loading'}
                        onClick={() => handleCuratorAction(notif, false)}
                        title={t('notifications.curatorReject')}
                        style={iconBtn('#ef4444', '#ef444444', '#ef444418')}
                      >
                        {state === 'loading' ? '…' : <IcoX />}
                      </button>
                    </div>
                  );
                })()}
                {/* Inline-кнопки: предложение выставить инвойс (слайс 2 демо) */}
                {notif.event_type === 'demo_invoice_offer' && (() => {
                  const state = invoiceActioned[notif.id];
                  if (state === 'accepted') return (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#22c55e', fontWeight: 500 }}>
                      ✅ {t('notifications.invoiceIssued')}
                    </div>
                  );
                  if (state === 'rejected') return (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>
                      {t('notifications.invoiceDeclined')}
                    </div>
                  );
                  // Гейт read_at убран намеренно: в демо ✓ остаётся доступной, пока инвойс
                  // не выставлен — даже если уведомление помечено прочитанным.
                  return (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                      <button
                        disabled={state === 'loading'}
                        onClick={() => handleInvoiceOffer(notif, true)}
                        title={t('notifications.invoiceAccept')}
                        style={iconBtn('#22c55e', '#22c55e44', '#22c55e18')}
                      >
                        {state === 'loading' ? '…' : <IcoCheck />}
                      </button>
                      {/* В демо отклонить инвойс нельзя — ✗ серая и недоступная (ведём к ✓). */}
                      <button
                        disabled
                        title={t('notifications.invoiceReject')}
                        style={{ ...iconBtn('#9ca3af', '#9ca3af44', '#9ca3af18'), cursor: 'not-allowed', opacity: 0.5 }}
                      >
                        <IcoX />
                      </button>
                    </div>
                  );
                })()}
                {/* Inline-кнопки: согласование расхода (слайс 3 демо) */}
                {notif.event_type === 'demo_expense_offer' && (() => {
                  const state = expenseActioned[notif.id];
                  if (state === 'accepted') return (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#22c55e', fontWeight: 500 }}>
                      ✅ {t('notifications.expenseApproved')}
                    </div>
                  );
                  if (state === 'rejected') return (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>
                      {t('notifications.expenseRejected')}
                    </div>
                  );
                  // Гейт read_at убран: ✓/✗ остаются доступными, пока расход не обработан.
                  // Но само согласование — по праву expenses.approve: без него
                  // кнопки не рисуем, иначе бэкенд ответит 403.
                  if (!canApproveExpenses) return null;
                  return (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button
                        disabled={state === 'loading'}
                        onClick={(e) => { e.stopPropagation(); handleExpenseOffer(notif, true); }}
                        title={t('notifications.expenseAccept')}
                        style={iconBtn('#22c55e', '#22c55e44', '#22c55e18')}
                      >
                        {state === 'loading' ? '…' : <IcoCheck />}
                      </button>
                      <button
                        disabled={state === 'loading'}
                        onClick={(e) => { e.stopPropagation(); handleExpenseOffer(notif, false); }}
                        title={t('notifications.expenseReject')}
                        style={iconBtn('#ef4444', '#ef444444', '#ef444418')}
                      >
                        {state === 'loading' ? '…' : <IcoX />}
                      </button>
                    </div>
                  );
                })()}
                {/* Inline-кнопка: поздравить клиента с ДР (та же логика и статус, что в карточке клиента) */}
                {notif.event_type === 'client_birthday' && (() => {
                  const cid = parseParams(notif).clientId;
                  const s = cid != null ? birthdayStatus[cid] : null;
                  if (!s || !s.email) return null; // нет email — слать поздравление некуда
                  const greeted = s.greeted_this_year;
                  return (
                    <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => openBirthday(notif)}
                        title={t('clientDetail.birthdayGreetBtn')}
                        style={{
                          ...iconBtn('#1a7f43', '#22c55e44', greeted ? '#22c55e10' : '#22c55e18'),
                          width: 'auto', padding: '0 12px', gap: 6, fontSize: 14, fontWeight: 600,
                          opacity: greeted ? 0.75 : 1,
                        }}
                      >
                        🎂 <span>{t('clientDetail.birthdayGreetBtn')}</span>
                        {greeted && <span style={{ color: '#22c55e' }}><IcoCheck size={14} /></span>}
                      </button>
                      {greeted && s?.birthday_greeted_by_name && (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)' }}>
                          ✓ {t('clientDetail.birthdayGreetedBy', { name: s.birthday_greeted_by_name })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="notif-item-meta">
                  <span className="notif-item-time">{timeAgo(notif.sent_at, t, i18n.language)}</span>
                  <span className="notif-item-channels">
                    {notif.channels.map(ch => (
                      <span key={ch} className="notif-item-channel">
                        {CHANNEL_LABELS[ch] || ch}
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {birthdayClient && (
        <BirthdayGreetingModal
          client={birthdayClient}
          onClose={() => setBirthdayClient(null)}
          onSent={() => {
            // Обновляем статус (✓) и список уведомлений после отправки.
            setBirthdayStatus(prev => ({ ...prev, [birthdayClient.id]: undefined }));
            api.getBirthdayGreetingStatus(birthdayClient.id)
              .then(s => setBirthdayStatus(prev => ({ ...prev, [birthdayClient.id]: s })))
              .catch(() => {});
            refresh?.();
          }}
        />
      )}

      {expenseModalNotif && (
        <ExpenseReviewModal
          params={parseParams(expenseModalNotif)}
          busy={expenseActioned[expenseModalNotif.id] === 'loading'}
          onApprove={() => { handleExpenseOffer(expenseModalNotif, true); setExpenseModalNotif(null); }}
          onReject={() => { handleExpenseOffer(expenseModalNotif, false); setExpenseModalNotif(null); }}
          onClose={() => setExpenseModalNotif(null)}
        />
      )}
    </>
  );
}

// ─── Рендер текста уведомления из params ─────────────────────────────────────
function renderNotifText(notif, t) {
  const p = notif.params
    ? (typeof notif.params === 'string' ? JSON.parse(notif.params) : notif.params)
    : null;

  if (!p) {
    // Старые записи без params — берём из БД или fallback
    return { title: notif.title || fallbackTitle(notif.event_type, t), body: notif.body };
  }

  let titleKey = null;
  let bodyKey  = null;

  if (notif.event_type === 'task_assigned') {
    if (p.role === 'admin' || p.role === 'superadmin') {
      titleKey = 'notifications.evt.task_assigned_admin_title';
      bodyKey  = 'notifications.evt.task_assigned_admin_body';
    } else {
      titleKey = 'notifications.evt.task_assigned_worker_title';
      bodyKey  = 'notifications.evt.task_assigned_worker_body';
    }
  } else if (notif.event_type === 'task_completed') {
    if (p.role === 'admin' || p.role === 'superadmin') {
      titleKey = 'notifications.evt.task_completed_admin_title';
      bodyKey  = 'notifications.evt.task_completed_admin_body';
    } else {
      titleKey = 'notifications.evt.task_completed_worker_title';
      bodyKey  = 'notifications.evt.task_completed_worker_body';
    }
  } else if (notif.event_type === 'task_deadline') {
    const dl = p.daysLeft;
    const deadlineLabel = dl === 0
      ? t('notifications.evt.deadlineToday')
      : dl === 1
        ? t('notifications.evt.deadlineTomorrow')
        : t('notifications.evt.deadlineInDays', { count: dl });
    titleKey = 'notifications.evt.task_deadline_title';
    bodyKey  = 'notifications.evt.task_deadline_body';
    const title = t(titleKey, { taskTitle: p.taskTitle, deadlineLabel });
    const body  = t(bodyKey,  { propName: p.propName });
    return { title, body };
  } else if (notif.event_type === 'task_overdue') {
    titleKey = 'notifications.evt.task_overdue_title';
    bodyKey  = 'notifications.evt.task_overdue_body';
  }

  if (notif.event_type === 'curator_change_request') {
    return {
      title: t('notifications.evt.curator_change_request_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.curator_change_request_body',  { requesterName: p.requesterName }),
    };
  }
  if (notif.event_type === 'curator_offer') {
    return {
      title: t('notifications.evt.curator_offer_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.curator_offer_body',  { offererName: p.offererName }),
    };
  }
  if (notif.event_type === 'curator_change_accepted') {
    return {
      title: t('notifications.evt.curator_change_accepted_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.curator_change_accepted_body',  { curatorName: p.curatorName }),
    };
  }
  if (notif.event_type === 'curator_change_rejected') {
    return {
      title: t('notifications.evt.curator_change_rejected_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.curator_change_rejected_body',  { curatorName: p.curatorName }),
    };
  }
  if (notif.event_type === 'demo_invoice_offer') {
    return {
      title: t('notifications.evt.demo_invoice_offer_title'),
      body:  t('notifications.evt.demo_invoice_offer_body', { taskTitle: p.taskTitle, propName: p.propName }),
    };
  }
  if (notif.event_type === 'demo_invoice_issued') {
    return {
      title: t('notifications.evt.demo_invoice_issued_title'),
      body:  t('notifications.evt.demo_invoice_issued_body', {
        clientName: p.clientName || '',
        invoiceNumber: p.invoiceNumber,
      }),
    };
  }
  if (notif.event_type === 'demo_expense_offer') {
    return {
      title: t('notifications.evt.demo_expense_offer_title', { workerName: p.workerName || '' }),
      body:  t('notifications.evt.demo_expense_offer_body', {
        amount: p.amount,
        currency: p.currency || 'AED',
        vendor: p.vendor || '',
        category: p.category || '',
      }),
    };
  }
  if (notif.event_type === 'quote_sent') {
    return {
      title: t('notifications.evt.quote_sent_title', { quoteNumber: p.quoteNumber || '' }),
      body:  t('notifications.evt.quote_sent_body', {
        clientName: p.clientName || '', amount: p.total, currency: p.currency || 'AED',
      }),
    };
  }
  if (notif.event_type === 'zoho_invoice_sent') {
    return {
      title: t('notifications.evt.zoho_invoice_sent_title', { invoiceNumber: p.invoiceNumber || '' }),
      body:  t('notifications.evt.zoho_invoice_sent_body', {
        clientName: p.clientName || '', amount: p.total, currency: p.currency || 'AED',
      }),
    };
  }
  if (notif.event_type === 'zoho_invoice_changed') {
    return {
      title: t('notifications.evt.zoho_invoice_changed_title', { invoiceNumber: p.invoiceNumber || '' }),
      body:  t('notifications.evt.zoho_invoice_changed_body', {
        oldTotal: p.oldTotal, newTotal: p.newTotal, currency: p.currency || 'AED',
        taskTitle: p.taskTitle || '',
      }),
    };
  }
  if (notif.event_type === 'zoho_invoice_payment_received') {
    const key = p.isFullyPaid ? 'zoho_invoice_payment_received_full' : 'zoho_invoice_payment_received_partial';
    return {
      title: t(`notifications.evt.${key}_title`, { invoiceNumber: p.invoiceNumber || '' }),
      body:  t(`notifications.evt.${key}_body`, {
        amount: p.amount, currency: p.currency || 'AED', balance: p.balance,
        taskTitle: p.taskTitle || p.propName || '',
      }),
    };
  }
  if (notif.event_type === 'zoho_invoice_customer_changed') {
    const body = t('notifications.evt.zoho_invoice_customer_changed_body', {
      oldCustomer: p.oldCustomer || '—', newCustomer: p.newCustomer || '—',
      taskTitle: p.taskTitle || p.propName || '',
    });
    return {
      title: t('notifications.evt.zoho_invoice_customer_changed_title', { invoiceNumber: p.invoiceNumber || '' }),
      // Контакт не нашёлся среди наших реквизитов — плательщик Работы остался
      // прежним. Умолчать нельзя: расхождение с Zoho сохраняется.
      body: p.matched === false
        ? `${body} · ${t('notifications.evt.zoho_invoice_customer_changed_unlinked', { brand: BRAND_NAME })}`
        : body,
    };
  }
  if (notif.event_type === 'demo_invoice_paid') {
    return {
      title: t('notifications.evt.demo_invoice_paid_title'),
      body:  t('notifications.evt.demo_invoice_paid_body', {
        invoiceNumber: p.invoiceNumber,
        amount: p.total,
        currency: p.currency || 'AED',
      }),
    };
  }
  if (notif.event_type === 'task_started') {
    return {
      title: t('notifications.evt.task_started_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_started_body',  { propName: p.propName, workerName: p.workerName }),
    };
  }
  // Исполнителю: его отметил Админ/Бригадир. Три заголовка на один event_type —
  // action приходит в params, отдельные типы плодить незачем.
  if (notif.event_type === 'assignee_status_set') {
    const key = p.action === 'pause' ? 'pause' : p.action === 'resume' ? 'resume' : 'start';
    return {
      title: t(`notifications.evt.assignee_status_${key}_title`, { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.assignee_status_body', { propName: p.propName, actorName: p.actorName }),
    };
  }
  if (notif.event_type === 'task_reminder') {
    return {
      title: t('notifications.evt.task_reminder_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_reminder_body',  { propName: p.propName, workerName: p.workerName }),
    };
  }
  if (notif.event_type === 'task_emergency_assigned') {
    return {
      title: t('notifications.evt.task_emergency_assigned_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_emergency_assigned_body',  { propName: p.propName }),
    };
  }
  if (notif.event_type === 'task_emergency_admin') {
    const curatorLine = p.curatorName
      ? t('notifications.evt.curator_line', { curatorName: p.curatorName })
      : t('notifications.evt.curator_needed');
    return {
      title: t('notifications.evt.task_emergency_admin_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_emergency_admin_body',  { propName: p.propName, curatorLine }),
    };
  }
  if (notif.event_type === 'task_emergency_curator') {
    return {
      title: t('notifications.evt.task_emergency_curator_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_emergency_curator_body',  { propName: p.propName, curatorName: p.curatorName }),
    };
  }
  if (notif.event_type === 'task_emergency_started') {
    return {
      title: t('notifications.evt.task_emergency_started_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_emergency_started_body',  { propName: p.propName, workerName: p.workerName }),
    };
  }
  if (notif.event_type === 'task_emergency_completed') {
    return {
      title: t('notifications.evt.task_emergency_completed_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_emergency_completed_body',  { propName: p.propName, workerName: p.workerName }),
    };
  }
  if (notif.event_type === 'task_emergency_escalation') {
    const curatorLine = p.curatorName
      ? t('notifications.evt.curator_line', { curatorName: p.curatorName })
      : t('notifications.evt.curator_needed');
    return {
      title: t('notifications.evt.task_emergency_escalation_title', { taskTitle: p.taskTitle }),
      body:  t('notifications.evt.task_emergency_escalation_body',  { propName: p.propName, count: p.hours, curatorLine }),
    };
  }
  if (notif.event_type === 'doc_expiry') {
    const titleKey = p.daysBefore === 0 ? 'notifications.evt.doc_expiry_title_0'
                   : p.daysBefore === 7 ? 'notifications.evt.doc_expiry_title_7'
                   :                      'notifications.evt.doc_expiry_title_30';
    return {
      title: t(titleKey),
      body:  t('notifications.evt.doc_expiry_body', { docList: p.docList, expiryDate: p.expiryDate }),
    };
  }

  // Внешние заявки: слева уже иконка (📨/🚨) — убираем ведущий эмодзи из заголовка,
  // чтобы не было дубля. Источник («… от Do Do Desk») бэкенд уже вшил в title.
  if (notif.event_type === 'request_received' || notif.event_type === 'request_emergency_received') {
    const raw = notif.title || fallbackTitle(notif.event_type, t);
    return { title: raw.replace(/^\s*[\p{Emoji_Presentation}\u{1F000}-\u{1FAFF}☀-➿]+\s*/u, ''), body: notif.body };
  }

  // Канал: заголовок — из i18n по причине (язык интерфейса, а не тот, на
  // котором ушёл пуш), тело — превью самого сообщения.
  if (notif.event_type === 'channel_message') {
    const reason = ['mention', 'reply', 'task'].includes(p.reason) ? p.reason : 'mention';
    return {
      title: t(`channel.notif.${reason}`, { author: p.authorName || '', num: p.taskNumber || '' }),
      body: p.snippet || notif.body,
    };
  }

  if (notif.event_type === 'client_birthday') {
    // В центре уведомлений слева уже есть иконка 🎂 — убираем дубль из заголовка
    // (в заголовке для push/email торт остаётся, там отдельной иконки нет).
    const raw = notif.title || fallbackTitle(notif.event_type, t);
    return { title: raw.replace(/^🎂\s*/, ''), body: notif.body };
  }

  if (!titleKey) {
    return { title: notif.title || fallbackTitle(notif.event_type, t), body: notif.body };
  }

  // Для task_assigned worker — дата опциональная
  const dateStr = p.date ? ' · ' + p.date : '';

  const title = t(titleKey, { ...p, dateStr });
  const body  = t(bodyKey,  { ...p, dateStr });
  return { title, body };
}

function fallbackTitle(event_type, t) {
  const MAP = {
    task_assigned:  t('notifications.taskAssigned'),
    task_completed: t('notifications.taskCompleted'),
    task_overdue:   t('notifications.taskOverdue'),
    test:           t('notifications.testNotif'),
  };
  return MAP[event_type] || t('notifications.fallback');
}
